import styled from "styled-components";
import React, { useState, useRef, useEffect } from "react";
import CourseForm from "./CourseForm";
import dayjs from 'dayjs';
import { useNavigate } from "react-router-dom";
import { HiDotsVertical, HiOutlinePencil, HiOutlineCog, HiOutlineDownload, HiOutlineTrash } from "react-icons/hi";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { generateCourseReport, downloadTextFile } from "../../utils/reportGenerator";
import { toast } from "sonner";
import { usePrivacy } from "../../contexts/PrivacyContext";
import { getCourseTimeZone } from "../../utils/courseSchedule";

const toDayjs = (d) => {
    if (!d) return null;
    if (typeof d?.toDate === 'function') return dayjs(d.toDate()); // Firestore Timestamp or Dayjs-like
    return dayjs(d); // Date or ISO string
};

const getProgressPct = (start, end) => {
    if (!start || !end) return null;
    const now = dayjs();

    const total = end.diff(start);   // ms
    if (total <= 0) return now.isAfter(end) ? 100 : 0;

    const elapsed = Math.min(Math.max(now.diff(start), 0), total);
    return Math.round((elapsed / total) * 100);
};

const CourseCard = ({ course, onUpdate, onDelete, onClick }) => {
    const { maskCourseName } = usePrivacy();
    const [menuVisible, setMenuVisible] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [editedCourse, setEditedCourse] = useState(course);
    const menuRef = useRef(null);
    const navigate = useNavigate();

    const toggleMenu = (e) => {
        e.stopPropagation();
        setMenuVisible(!menuVisible);
    };

    const timeZone = getCourseTimeZone(course);
    const start = toDayjs(course.startDate)?.tz(timeZone);
    const end = toDayjs(course.endDate)?.tz(timeZone);
    const pct = getProgressPct(start, end);
    const pctSafe = Number.isFinite(pct) ? pct : 0;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuVisible(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuVisible]);


    const handleDelete = (e) => {
        e.stopPropagation();
        setIsConfirmingDelete(true);
    };

    const confirmDelete = () => {
        onDelete(course.id);
        setIsConfirmingDelete(false);
        setMenuVisible(false);
    };

    const cancelDelete = () => {
        setIsConfirmingDelete(false);
    };

    const handleManage = (e) => {
        e.stopPropagation();
        navigate(`/admin/home/group_manager`, { state: { course } });
    };

    const handleSettings = (e) => {
        e.stopPropagation();
        navigate(`/admin/course-settings/${course.id}`);
    };

    const handleDownloadReport = (e) => {
        e.stopPropagation();
        setMenuVisible(false);
        setIsDownloading(true);
        const safeName = course.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const promise = generateCourseReport(course.id).then(report => {
            downloadTextFile(report, `${safeName}_report.txt`);
        }).finally(() => setIsDownloading(false));
        toast.promise(promise, {
            loading: 'Generating report…',
            success: 'Report downloaded!',
            error: 'Failed to generate report. Please try again.',
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedCourse((prev) => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (field, value) => {
        setEditedCourse((prev) => ({ ...prev, [field]: value }));
    };

    const handleUpdate = async () => {
        setIsSubmitting(true);
        await onUpdate(editedCourse);
        setIsSubmitting(false);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditedCourse(course);
        setIsEditing(false);
    };

    return isEditing ? (
        <CourseForm
            course={editedCourse}
            onInputChange={handleInputChange}
            onDateChange={handleDateChange}
            onSubmit={handleUpdate}
            onCancel={handleCancel}
            submitButtonText="Update"
            isSubmitting={isSubmitting}
        />
    ) : (
        <>
            <CardContainer onClick={onClick}>
                <TopBar>
                    <CourseTitle>{maskCourseName(course.name, course.id)}</CourseTitle>
                    <MenuWrap ref={menuRef}>
                        <MenuButton
                            onClick={toggleMenu}
                            aria-haspopup="menu"
                            aria-expanded={menuVisible}
                            aria-label="More options"
                        >
                            <HiDotsVertical />
                        </MenuButton>

                        <DropdownMenu role="menu" $open={menuVisible}>
                            <MenuItem role="menuitem" onClick={handleManage}><HiOutlinePencil />Manage</MenuItem>
                            <MenuItem role="menuitem" onClick={handleSettings}><HiOutlineCog />Settings</MenuItem>
                            <MenuItem role="menuitem" onClick={handleDownloadReport} disabled={isDownloading}>
                                <HiOutlineDownload />{isDownloading ? 'Generating…' : 'Download Report'}
                            </MenuItem>
                            <MenuDivider />
                            <MenuItem role="menuitem" className="destructive" onClick={handleDelete}>
                                <HiOutlineTrash />Delete
                            </MenuItem>
                        </DropdownMenu>
                    </MenuWrap>
                </TopBar>
                <CourseDescription>{course.description}</CourseDescription>
                {end ? (
                    <ProgressBlock>
                        <ProgressLabels>
                            <span title={start?.format('MMM D, YYYY h:mm A z')}>{start ? start.format('YYYY-MM-DD') : '—'}</span>
                            <CenterPct>{pctSafe}%</CenterPct>
                            <span title={end.format('MMM D, YYYY h:mm A z')}>{end.format('YYYY-MM-DD')}</span>
                        </ProgressLabels>

                        <ProgressTrack
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={pct}
                            aria-label="Course progress"
                        >
                            <ProgressFill $pct={pctSafe} />
                        </ProgressTrack>

                        <StatusRow>{course.isActive ? 'In Progress' : 'Inactive'}</StatusRow>
                    </ProgressBlock>
                ) : (
                    <CardBottom>
                        <MetaRow>{start ? start.format('YYYY-MM-DD') : 'No start date'}</MetaRow>
                        <MetaRow>{course.isActive ? 'In Progress' : 'Inactive'}</MetaRow>
                    </CardBottom>
                )}
            </CardContainer>

            {isConfirmingDelete && (
                <ConfirmDialog
                    title="Delete Course?"
                    message={<>Are you sure you want to delete <strong>"{course.name}"</strong>? All your progress and materials will be removed.</>}
                    confirmText="Yes, Delete"
                    cancelText="No, Go Back"
                    variant="danger"
                    showIcon
                    footnote="This action cannot be undone."
                    onConfirm={confirmDelete}
                    onCancel={cancelDelete}
                />
            )}
        </>
    );
};

const CardContainer = styled.div`
    background-color: white;
    border-radius: 10px;
    padding: 20px;
    width: 300px;
    height: 266px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    position: relative;
    overflow: visible; 
    display: flex;
    flex-direction: column;
    cursor: pointer;
    transition: transform 0.2s;
    &:hover {
        transform: scale(1.05);
    }
`;

const TopBar = styled.div`
    display: flex;
    justify-content: space-between;
    //align-items: center;
    max-height: 48%;
    width: 100%;
    align-items: flex-start;
`;

const MenuWrap = styled.div`
  position: relative;         /* anchor the dropdown to this wrapper */
  z-index: 5;                 /* above card content */
`;

const CourseTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    width: 90%;
    text-align: left;
    text-overflow: ellipsis;
    overflow: hidden;
    word-break: break-all;
    overflow-y: auto;
    overscroll-behavior: contain;
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;             /* gray-500/600 */
  cursor: pointer;

  svg { font-size: 20px; }

  &:hover { background: #f3f4f6; color: #374151; } /* subtle hover */
  &:focus-visible {
    outline: 2px solid #6366f1;   /* indigo-500 */
    outline-offset: 2px;
  }
`;

const MenuIcon = styled.div`
    cursor: pointer;
    font-size: 24px;
    user-select: none;
    font-weight: bold;
    z-index: 101;
    padding: 5px;
    color: #737475ff;    /* gray-700 */
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;


    &:hover {
        color: #111827;  /* darker on hover */
    }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 36px;                 /* just below the button */
  right: 0;
  min-width: 120px;
  padding: 6px;
  background: #fff;
  border: 1px solid #e5e7eb; /* gray-200 */
  border-radius: 10px;
  box-shadow:
    0 10px 20px rgba(0,0,0,.08),
    0 2px 6px rgba(0,0,0,.04);
  transform-origin: top right;
  transition: opacity .12s ease, transform .12s ease, visibility 0s linear .12s;
  z-index: 10;

  /* animated open/close via prop */
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: ${({ $open }) => ($open ? "scale(1)" : "scale(0.98)")};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  ${({ $open }) => $open && `transition-delay: 0s;`};  /* show immediately when open */
`;

const MenuItem = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;

  svg { font-size: 18px; flex-shrink: 0; }
  border: 0;
  background: transparent;
  border-radius: 8px;
  font-size: 14px;
  color: ${({ disabled }) => disabled ? '#9ca3af' : '#374151'};
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  text-align: left;
  white-space: nowrap;
  pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};

  &:hover { background: #f9fafb; }        /* subtle hover */
  &:active { background: #eef2ff; }       /* indigo-50 */
  &:focus-visible {
    outline: 2px solid #9333ea;
    outline-offset: 2px;
  }

  &.destructive {
    color: #b91c1c;                       /* red-700 */
  }
  &.destructive:hover {
    background: #fef2f2;                  /* red-50 */
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: #f3f4f6; /* gray-100 */
  margin: 4px 6px;
`;


const CourseDescription = styled.p`
    font-size: 14px;
    color: #555;
    text-align: left;
    flex-grow: 1;
    margin: 10px 0;
    overflow: hidden;
    word-break: break-word;
    text-overflow: ellipsis;
    overflow-y: auto;
`;

const CourseDate = styled.p`
    font-size: 12px;
    color: #888;
    margin: 0;
`;


const MetaRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.875rem;  /* text-sm */
  color: #6b7280;       /* gray-500 */
  gap: 0.25rem;
  font-weight: 600;
`;


const CardBottom = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-top: auto; 
`;

const ProgressBlock = styled.div`
  margin-top: auto;         /* keep at bottom of card */
  width: 100%;
`;

const ProgressLabels = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 0.825rem;
  color: #6b7280; /* gray-500 */
`;

const CenterPct = styled.span`
  font-weight: 700;
  font-size: 1rem;
  color: #9333ea; /* gray-700 */
`;

const ProgressTrack = styled.div`
  position: relative;
  height: 10px;
  border-radius: 9999px;
  background: #f3f4f6; /* gray-100 */
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct = 0 }) => `${Math.max(0, Math.min(100, $pct))}%`};
  background: linear-gradient(90deg, #cea1f6ff, #9333ea);
  border-radius: inherit;
  transition: width .25s ease;
`;


const StatusRow = styled.div`
  margin-top: 6px;
  font-size: 0.825rem;
  color: #6b7280;
  font-weight: 600;
`;


export default CourseCard;
